
import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Connection, Organization, Provider } from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import { AttentionIcon, CopyToClipboard, HideString, ShowString } from "../common/ProjectIcons";
import { toast } from 'react-toastify';
import { buttonClasses, inputClasses } from "../common/tailwindClassnames";

type ConnectionOverviewProps = {
    connection: Connection,
    currentMemberIsAdmin: boolean,
    errors: any,
    apiKey: string,
    connectionProvider: Provider,
    onChangeConnectionName: Function,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChangeConnectionOwner: Function,
    onUpdateConnection: MouseEventHandler,
    onCancelConnectionChanges: MouseEventHandler,
    changesMade: boolean,
}

const ConnectionDetailsOverview = ({
    connection,
    currentMemberIsAdmin,
    errors,
    apiKey,
    connectionProvider,
    onChangeConnectionName,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChangeConnectionOwner,
    onUpdateConnection,
    onCancelConnectionChanges,
    changesMade,
}: ConnectionOverviewProps
) => {
    const { t } = useTranslation();
    const [keyVisible, setKeyVisible] = useState(false);
    const toggleVisibility = () => setKeyVisible(!keyVisible)
    return (
        <div className="mt-8">
            {/* div with configuration details */}
            <div className="flex flex-row w-4/5">
                <div className="flex flex-col w-full mr-3">
                    <label
                        className="inline-block hover:cursor-pointer text-secondary-gray dark:text-gray-lines text-sm"
                    >
                        {t("Connection Name")} *
                    </label>
                    <input
                        id="name"
                        placeholder=""
                        value={connectionProvider.name}
                        className={inputClasses.standard}
                        onChange={(e) => onChangeConnectionName(e)}
                        disabled={false}
                    >
                    </input>
                    {errors.provider_name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.provider_name}</p></div>}
                    <div className="flex flex-row items-end w-full">
                        <div className="flex flex-col w-full">
                            <label
                                className="inline-block hover:cursor-pointer text-secondary-gray dark:text-gray-lines text-sm mt-5"
                            >
                                {t("API Key")}
                            </label>
                            <div className="flex flex-row items-center gap-2 w-full">
                                <div className="relative flex-1 min-w-0">
                                    <input
                                        id="apikey"
                                        placeholder=""
                                        value={apiKey}
                                        className={`${inputClasses.disabled} pr-10 w-full`}
                                        type={keyVisible ? "text" : "password"}
                                        disabled={true}
                                    />
                                    {currentMemberIsAdmin && (
                                        <button
                                            type="button"
                                            onClick={toggleVisibility}
                                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-off-black dark:text-white focus:outline-none"
                                            aria-label={keyVisible ? "Hide API key" : "Show API key"}
                                        >
                                            {keyVisible ? <HideString /> : <ShowString />}
                                        </button>
                                    )}
                                </div>
                                {currentMemberIsAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(apiKey);
                                            toast.info(`${t("API Key")} ${t("copied to clipboard")}`, {
                                                position: toast.POSITION.TOP_RIGHT,
                                                autoClose: 2000
                                            });
                                        }}
                                        className="flex h-full items-center justify-center rounded border border-neutral-gray-300 px-3 text-off-black dark:text-white flex-shrink-0"
                                        aria-label="Copy API key"
                                    >
                                        <CopyToClipboard />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col w-full ml-3">
                    <label
                        className="inline-block hover:cursor-pointer text-secondary-gray dark:text-gray-lines text-sm"
                    >
                        {t("Workspace")} *
                    </label>
                    <SelectInput
                        options={organizations.map((org: Organization) => {
                            return {
                                label: org.name,
                                value: org.id
                            }
                        })}
                        className={"basic-single dark:text-white"}
                        value={{ label: connection?.owner?.name || '', value: connection?.owner?.id || '' }}
                        autoSelectSingleOption={true}
                        isDisabled={false}
                        isLoading={false}
                        isClearable={false}
                        isSearchable={true}
                        isMulti={false}
                        name={t("Workspace")}
                        onChange={(e: any) => onChangeConnectionOwner(e)}
                        onMenuScrollToBottom={loadMoreOrgs}
                        handleInputChange={orgInputChange}
                    />
                    {errors.provider_owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.provider_owner}</p></div>}
                </div>
            </div>
            {/* buttons */}
            <div className="w-full z-10 fixed bottom-0 mt-12 mr-6 py-3 w-full flex flex-row bg-white/10 dark:bg-off-black/10 backdrop-blur transition-all duration-300">
                <button
                    className={changesMade ? `${buttonClasses.primary} mr-3` : `${buttonClasses.disabled} mr-3`}
                    disabled={!changesMade}
                    onClick={onUpdateConnection}>
                    {t("Save")}
                </button>
                <button
                    className={`${buttonClasses.textButton} px-3 mx-3`}
                    onClick={onCancelConnectionChanges}
                >
                    {t("Cancel")}
                </button>
            </div>
        </div>
    )
};

export default ConnectionDetailsOverview;